import {
  DECLINE_THRESHOLD_PCT,
  NotificationCategory,
  NotificationPreferenceKey,
  NotificationReadState,
  NotificationSeverity,
} from '@trulyinbox/types';
import { apiFetch } from './api';

// ── Domain constants (single source of truth) ───────────────────────────────

// The `NotificationCategory` / `NotificationReadState` enums are owned by
// `@trulyinbox/types` (shared with the backend producers + consumer). Re-export
// them here so this module stays the single import site for the notifications
// frontend (search-schema, filters, helpers, queries all import from here).
export {
  DECLINE_THRESHOLD_PCT,
  NotificationCategory,
  NotificationPreferenceKey,
  NotificationReadState,
  NotificationSeverity,
};

/**
 * The notification categories as an iterable, derived from the shared enum so
 * adding a category upstream flows through with no edit here. Used as the
 * `category` filter vocabulary in the URL search-schema and to render the feed
 * filter chips + preferences matrix. (String enums have no numeric reverse
 * map, so `Object.values` yields exactly the value strings.)
 */
export const NOTIFICATION_CATEGORIES: NotificationCategory[] =
  Object.values(NotificationCategory);

/** Read-state filter buckets as an iterable, derived from the shared enum. */
export const NOTIFICATION_READ_STATES: NotificationReadState[] =
  Object.values(NotificationReadState);

// ── Wire types ───────────────────────────────────────────────────────────────

/**
 * A single in-app notification. Dates arrive as ISO strings over the wire
 * (`apiFetch` returns the raw JSON body; the backend serializes `Date` to a
 * string), so `readAt`/`createdAt` are typed as `string` and converted via
 * `lib/date`'s `toDate` at the render boundary.
 */
export type NotificationDto = {
  id: number;
  type: string;
  category: NotificationCategory | null;
  /** Alert severity derived server-side from `type`; null = a plain notification. */
  severity: NotificationSeverity | null;
  title: string;
  body: string | null;
  /** The single email account this notification is about, or null (e.g. a bulk-op completion summary). */
  emailAccountId: number | null;
  /** Set once that email account is deleted; the mailbox link is then disabled. Null while it exists. */
  emailAccountDeletedAt: string | null;
  readAt: string | null;
  createdAt: string;
};

/** A cursor-paginated page of notifications (newest first). */
export type PaginatedNotifications = {
  data: NotificationDto[];
  cursor: string | null;
  hasMore: boolean;
};

/**
 * One settings-page switch pair's preferences (`GET /notifications/preferences`
 * returns every key, defaults filled). `locked` keys (BILLING) are always on and
 * refuse writes. `declineThresholdPct` is set only on DELIVERABILITY_DECLINING.
 */
export type NotificationPreference = {
  key: NotificationPreferenceKey;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  locked: boolean;
  declineThresholdPct: number | null;
};

/** One `PUT /notifications/preferences` row. An omitted threshold keeps the stored one. */
export type NotificationPreferenceInput = {
  key: NotificationPreferenceKey;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  declineThresholdPct?: number | null;
};

export type ListNotificationsParams = {
  category?: NotificationCategory[];
  readState?: NotificationReadState;
  cursor?: string;
  limit?: number;
};

// ── Request functions ────────────────────────────────────────────────────────

/** `GET /notifications` — cursor-paginated feed, scoped server-side to the
 * active org + workspace via the JWT. `category` is a repeatable query param. */
export async function listNotifications(
  params: ListNotificationsParams = {},
): Promise<PaginatedNotifications> {
  const search = new URLSearchParams();
  for (const c of params.category ?? []) search.append('category', c);
  if (params.readState && params.readState !== NotificationReadState.ALL) {
    search.set('readState', params.readState);
  }
  if (params.cursor) search.set('cursor', params.cursor);
  if (params.limit != null) search.set('limit', String(params.limit));
  const qs = search.toString();
  return apiFetch<PaginatedNotifications>(`/notifications${qs ? `?${qs}` : ''}`);
}

/** `GET /notifications/unread-count` — workspace-scoped badge count. */
export async function getUnreadCount(): Promise<{ count: number }> {
  return apiFetch<{ count: number }>('/notifications/unread-count');
}

/** `PATCH /notifications/:id/read` — mark one read (204). */
export async function markNotificationRead(id: number): Promise<void> {
  await apiFetch<void>(`/notifications/${id}/read`, { method: 'PATCH' });
}

/** `PATCH /notifications/read` — mark a specific set of notifications read (204). */
export async function markNotificationsRead(ids: number[]): Promise<void> {
  await apiFetch<void>('/notifications/read', {
    method: 'PATCH',
    body: JSON.stringify({ ids }),
  });
}

/** `PATCH /notifications/read-all` — mark every notification read (204). */
export async function markAllNotificationsRead(): Promise<void> {
  await apiFetch<void>('/notifications/read-all', { method: 'PATCH' });
}

/** `GET /notifications/preferences` — the key × channel matrix (defaults filled). */
export async function getNotificationPreferences(): Promise<NotificationPreference[]> {
  return apiFetch<NotificationPreference[]>('/notifications/preferences');
}

/** `PUT /notifications/preferences` — upsert one or more key rows; returns the full matrix. */
export async function updateNotificationPreferences(
  rows: NotificationPreferenceInput[],
): Promise<NotificationPreference[]> {
  return apiFetch<NotificationPreference[]>('/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(rows),
  });
}
